import { env } from '@/lib/env';
import { authService } from './authService';
import { CSRF_FAILURE_HEADER, csrfHeaders, isCsrfFailure, refreshCsrfToken } from './csrf';
import { refreshSession } from './sessionRefresh';

const API_BASE = `${env.API_BASE}/api`;

export const getApiUrl = (endpoint: string): string => {
  return `${API_BASE}/${endpoint}`;
};

/**
 * Thrown on a 403 from the API: the user is authenticated but not authorized for this resource
 * (e.g. a project they have no grant on). Distinct from a 401 (not signed in → redirect to login):
 * a 403 is a real authorization denial, so a PRIMARY page load should route to the dedicated
 * `/unauthorized` (Access Denied) page, while a SECONDARY/optional fetch can catch this and render an
 * inline "no access" state instead of ejecting the user from a page they can otherwise see.
 */
export class ForbiddenError extends Error {
  readonly status = 403;
  constructor(message = 'Forbidden') {
    super(message);
    this.name = 'ForbiddenError';
  }
}

/**
 * Thrown for any non-2xx the wrappers don't special-case (i.e. everything except the
 * 401 redirect and the 403 ForbiddenError). Carries the numeric `status` and the parsed
 * server `detail` so callers can classify a failure (transient 5xx → retry, 409 → conflict,
 * 400/422 → validation) instead of string-matching the message. Additive: extends `Error`
 * with the SAME `message` string apiError used to produce, so existing `catch (e)` callers
 * and any UI matching on the message are unaffected.
 */
export class ApiError extends Error {
  readonly status: number;
  readonly detail?: string;
  constructor(status: number, message: string, detail?: string) {
    super(message);
    this.name = 'ApiError';
    this.status = status;
    this.detail = detail;
  }
}

/**
 * Build an {@link ApiError} that includes the server's response body when present.
 * The semantic layer (and Spring's default error handler) return a descriptive
 * `{ error | message }` on 4xx — e.g. "unknown property", "ambiguous join needs
 * via" — which an opaque "API Error: 400" would otherwise hide. Keeps the
 * `API Error: <status> <statusText>` prefix as a fallback. Consumes the body, so
 * call only on the error path.
 */
async function apiError(response: Response): Promise<ApiError> {
  const base = `API Error: ${response.status} ${response.statusText}`;
  try {
    const text = await response.text();
    if (!text) return new ApiError(response.status, base);
    let detail: string | undefined;
    try {
      const body = JSON.parse(text) as { error?: unknown; message?: unknown; detail?: unknown };
      // `detail` is a problem-detail body (Spring's ResponseStatusException reason), e.g. a refused publish.
      const field = [body.error, body.message, body.detail].find((f) => typeof f === 'string' && f.trim() !== '');
      detail = typeof field === 'string' && field.trim() !== '' ? field.trim() : undefined;
    } catch {
      // Non-JSON body (plain text / HTML error page) — use it verbatim, bounded.
      detail = text.trim().slice(0, 500) || undefined;
    }
    return new ApiError(response.status, detail ? `${base}: ${detail}` : base, detail);
  } catch {
    return new ApiError(response.status, base);
  }
}

/**
 * `fetch` with the auth cookies, the CSRF token on state-changing methods, and two single retries:
 *
 * - after a **401** — the ID token has expired — `refreshSession()` (one shared request however
 *   many calls hit the wall together) and, if the sign-in was renewed, the same request again. A
 *   refusal leaves the 401 standing and the wrapper signs the user out via {@link signedOut}; an
 *   identity-provider outage throws {@link SessionRefreshUnavailableError} instead, because that is
 *   not a sign-out.
 * - after a **response the server marked as a CSRF failure** (`X-CSRF-Failure: true` — a stale
 *   token, e.g. the cookie was renewed in another tab): restock the token from `/auth/me`, then
 *   retry. Checked before the 401 path, since the marker can arrive on a 401.
 *
 * Any other 403 is returned untouched — it is a real authorization denial. All API calls, including
 * the few raw-fetch call sites outside this file, should go through here.
 */
export async function csrfFetch(url: string, init: RequestInit = {}): Promise<Response> {
  const send = () => {
    const headers = new Headers(init.headers);
    for (const [name, value] of Object.entries(csrfHeaders(init.method))) headers.set(name, value);
    return fetch(url, { ...init, credentials: 'include', headers });
  };
  let response = await send();
  // The CSRF marker first: a marked response is a token-restock problem whatever its status, and
  // must not be mistaken for an expired sign-in (session-timeout-fixes S2).
  if (isCsrfFailure(response.status, response.headers.get(CSRF_FAILURE_HEADER)) && (await refreshCsrfToken())) {
    response = await send();
  }
  if (response.status === 401 && !isCsrfFailure(response.status, response.headers.get(CSRF_FAILURE_HEADER))) {
    const outcome = await refreshSession();
    if (outcome === 'renewed') response = await send();
    else if (outcome === 'unavailable') throw new SessionRefreshUnavailableError();
    // 'refused': the 401 stands and the wrapper below signs the user out.
  }
  return response;
}

/**
 * Thrown when a request met an expired token and the sign-in could NOT be renewed because the
 * identity provider was unavailable — not because the sign-in is over. A transient failure like any
 * 503: the caller shows an error and the user can retry; nobody is signed out.
 */
export class SessionRefreshUnavailableError extends ApiError {
  constructor() {
    super(503, 'API Error: 503 Sign-in could not be renewed (identity provider unavailable)');
    this.name = 'SessionRefreshUnavailableError';
  }
}

/**
 * A 401 that survived the refresh-and-retry: the sign-in is over (the idle grace passed while the
 * tab slept, or the refresh token was revoked). Sign out properly rather than just bouncing to `/`,
 * so the cookies are cleared and Cognito's own session ends too; the landing page explains why.
 */
export function signOutOnExpiredSession(): void {
  authService.logout('timeout');
}

export async function apiFetch<T>(
  endpoint: string,
  options?: RequestInit
): Promise<T> {
  const url = getApiUrl(endpoint);
  const response = await csrfFetch(url, {
    ...options,
    headers: {
      'Content-Type': 'application/json',
      ...options?.headers,
    },
  });

  if (!response.ok) {
    if (response.status === 401) {
      signOutOnExpiredSession();
    }
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    throw await apiError(response);
  }

  // 204 No Content (and other empty bodies) — common for write endpoints — can't be JSON-parsed.
  if (response.status === 204) {
    return undefined as T;
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : undefined) as T;
}

/**
 * GET that resolves to null on 404 instead of throwing — for "read or absent" endpoints
 * (e.g. the semantic READ-1/READ-2 reads, which 404 when the document isn't in the project).
 */
export async function apiGetOrNull<T>(endpoint: string): Promise<T | null> {
  const response = await csrfFetch(getApiUrl(endpoint), {
    headers: { 'Content-Type': 'application/json' },
  });
  if (response.status === 404) {
    return null;
  }
  if (!response.ok) {
    if (response.status === 401) {
      signOutOnExpiredSession();
    }
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    throw await apiError(response);
  }
  const text = await response.text();
  return (text ? JSON.parse(text) : null) as T | null;
}

export async function apiPost<T, D = unknown>(
  endpoint: string,
  data: D
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'POST',
    body: JSON.stringify(data),
  });
}

export async function apiPut<T, D = unknown>(
  endpoint: string,
  data: D
): Promise<T> {
  return apiFetch<T>(endpoint, {
    method: 'PUT',
    body: JSON.stringify(data),
  });
}

// DELETE responses are 204 No Content. Optionally carries a JSON body (some endpoints take a list of
// items to remove). Raw fetch (not apiFetch) since there's no response body to parse.
export async function apiDelete(endpoint: string, data?: unknown): Promise<void> {
  const url = getApiUrl(endpoint);
  const response = await csrfFetch(url, {
    method: 'DELETE',
    ...(data !== undefined
      ? { headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(data) }
      : {}),
  });

  if (!response.ok) {
    if (response.status === 401) {
      signOutOnExpiredSession();
    }
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    throw await apiError(response);
  }
}

export async function apiPostFormData<T>(
  endpoint: string,
  formData: FormData
): Promise<T> {
  const url = getApiUrl(endpoint);
  // No Content-Type: the browser sets the multipart boundary. The CSRF header still travels.
  const response = await csrfFetch(url, {
    method: 'POST',
    body: formData,
  });

  if (!response.ok) {
    if (response.status === 401) {
      signOutOnExpiredSession();
    }
    if (response.status === 403) {
      throw new ForbiddenError();
    }
    throw await apiError(response);
  }

  return response.json();
}

export { API_BASE };
